import React, {useContext, useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {Alert, Button, IconButton, Tooltip, Typography} from '@mui/material'
import {toast} from 'react-toastify'
import Background from '../../../assets/background.svg'
import {HistoryContext} from '../../../contexts/history'
import {DocumentApi} from '../../../services/DocumentApi.service'
import CloseIcon from '@mui/icons-material/Close'
import {getCurrentColors} from '../../../utils/colors'
import {DocumentResume} from './components/DocumentResume'
import {ResumeItem} from '../../../types/resume'
import {Project} from '../../../types/getDocuments'
import QuestionRenderer from './components/QuestionRenderer'
import {classificationExpired} from './classificationTimeout'
import {
    FormData,
    FormDataValue,
    QuestionType,
    RowData,
    UploadedDocumentContent,
} from './types'
import InfoIcon from '@mui/icons-material/Info'
import { useDebounce } from 'use-debounce';

const COLORS = getCurrentColors()

const formatCurrency = (value: number): string => {
    return value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
}

const parseCurrencyInput = (value: string): number => {
    const cleanValue = value.replace(/[R$\s.]/g, '').replace(',', '.')
    return Number(cleanValue) || 0
}

const isAnswerContentEmpty = (content: Record<string, any>) => {
    const entries = Object.entries(content)

    if (entries.length === 0) {
        return true
    }

    return entries.every(([, value]) => {
        if (Array.isArray(value)) {
            return value.length === 0
        }

        return value === undefined || value === null || value === ''
    })
}

const getDocumentUploadStatus = (document: UploadedDocumentContent) => {
    const status =
        document.status ||
        (document.content && document.content.trim() ? 'processed' : 'uploaded')

    // LCTO-1614: 'classifying' = a IA esta identificando qual documento do fluxo
    // e o arquivo. Para o polling e para o "Proximo" isso e' o mesmo que
    // 'processing' -- sem esta linha o laco de consulta PARAVA assim que a
    // extracao terminava, e a sugestao da IA nunca chegava na tela (o card
    // ficava preso em "Identificando documento..." para sempre). Passado o teto
    // de espera, destrava: o texto ja foi extraido e o arquivo e' utilizavel.
    if (status === 'classifying') {
        return classificationExpired(document.classification_requested_at)
            ? 'processed'
            : 'processing'
    }

    // 'uploaded' = arquivo no S3 aguardando a extracao da Lambda document-uploader
    // (o callback ainda nao chegou). Para o front isso e' indistinguivel de
    // 'processing': mantem o polling ativo e o "Proximo" bloqueado ate o callback
    // gravar o content (status 'processed'). Sem normalizar, o polling nunca
    // ativaria, pois o backend deixa o doc em 'uploaded' por design.
    return status === 'uploaded' ? 'processing' : status
}

const canSubmitDocumentUploadAnswer = (
    documents: UploadedDocumentContent[]
) =>
    documents.length > 0 &&
    documents.every((document) => {
        const status = getDocumentUploadStatus(document)

        // "Proximo" so habilita quando o arquivo foi processado (status server-owned).
        return status === 'processed' && Boolean(document.content?.trim())
    })

const hasProcessingDocumentUpload = (question?: QuestionType) =>
    Boolean(
        question?.question_type === 'document_upload' &&
            question.answer_content?.documents?.some(
                (document) => getDocumentUploadStatus(document) === 'processing'
            )
    )

const createEmptyRow = (
    id: number,
    isBooleanTable: boolean = false,
    documento: string = ''
): RowData => {
    if (isBooleanTable) {
        return {
            id,
            documento,
            bool: 'Não',
            descricao: '',
        }
    }
    return {
        id,
        name: '',
        quantity: '',
        price: '',
        total_price: '0',
    }
}

const Footer = styled.div`
    height: 57px;
    width: 100%;
    background-color: #ffffff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    position: absolute;
    bottom: 0;
    left: 0;
`

const GradientButton = styled.button`
    background: linear-gradient(90deg, #1db8e8 0%, #26e87d 100%);
    border: none;
    color: white;
    padding: 8px 16px;
    font-size: 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        opacity: 0.9;
    }
`

const Page = styled.div`
    height: 100vh;
    margin-top: 0;
    padding: 28px 40px;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: auto;
    background-color: #f6f8fa;
    background-image: url(${Background});
    background-repeat: no-repeat;
    background-position: right 370px;
    background-size: 800px;
    flex: 1;
`

const Header = styled.div`
    width: 100%;
    margin-bottom: 32px;
`

const ContentContainer = styled.div`
    width: 70%;
    max-width: 1000px;
    background-color: #ffffff;
    padding: 32px;
    border-radius: 10px;
    margin-bottom: 24px;
    align-self: center;
`

const NavigationButtons = styled.div`
    width: 70%;
    max-width: 1000px;
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin: 16px auto 48px;
`

const EDITING_PROJECT_ID_KEY = 'documentGeneratorEditingProjectId'
const EDITING_QUESTION_ID_KEY = 'documentGeneratorEditingQuestionId'
const EDITING_MODE_KEY = 'documentGeneratorEditingMode'

const readStorage = (key: string): string | null => {
    if (typeof window === 'undefined') {
        return null
    }

    return localStorage.getItem(key)
}

const clearEditingContext = () => {
    if (typeof window === 'undefined') {
        return
    }

    localStorage.removeItem(EDITING_PROJECT_ID_KEY)
    localStorage.removeItem(EDITING_QUESTION_ID_KEY)
    localStorage.removeItem(EDITING_MODE_KEY)
}

export default function DocumentGenerator() {
    const navigate = useNavigate()
    const {
        questionArray,
        setQuestionArray,
        limits,
        projectCost,
        user,
        viewResume,
        setViewResume,
    } = useContext(HistoryContext)
    const [projectId, setProjectId] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const [finished, setFinished] = useState(false)
    const [edit, setEdit] = useState(() => readStorage(EDITING_MODE_KEY) === 'true')
    const api = useMemo(() => new DocumentApi(), [])
    const [projectName, setProjectName] = useState('')
    const [activeStep, setActiveStep] = useState(0)
    const [newTables, setNewTables] = useState<RowData[][]>(
        questionArray[activeStep]?.answer_content?.table || [
            [createEmptyRow(1)],
        ]
    )
    const [formData, setFormData] = useState<FormData>({})
    const [finish, setFinish] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [generatingProject, setGeneratingProject] = useState(false)
    const [documentUploadPendingByQuestionId, setDocumentUploadPendingByQuestionId] =
        useState<Record<string, boolean>>({})
    const [debouncedFormData] = useDebounce(formData, 3000)

    useEffect(() => {
        if (Object.keys(debouncedFormData).length === 0) return

        const currentQuestion = questionArray[activeStep] as QuestionType
        if (!currentQuestion) return

        // document_upload: o arquivo vai direto pro S3 e o status do documento
        // (processing/processed) e' propriedade do servidor, atualizado pelo
        // callback do analyzer. Regravar o rascunho aqui com o formData defasado
        // sobrescreve o status que o callback acabou de gravar (trava em
        // "Processando..."). O envio real acontece no postAnswer do "Proximo".
        if (currentQuestion.question_type === 'document_upload') return

        const questionId = currentQuestion.id
        const content = buildAnswerContent(currentQuestion)

        api.draftAnswers({
            content,
            question_id: questionId,
            project_id: projectId,
            answered: isAnswerContentEmpty(content) ? "false" : "true",
        }).catch(console.error)
    }, [debouncedFormData])

    useEffect(() => {
        if (viewResume) {
            setIsLoading(false)
            setFinished(true)
            ResumeDoc()
        }
    }, [viewResume])

    useEffect(() => {
        const currentQuestion = questionArray[activeStep] as QuestionType

        if (currentQuestion && currentQuestion.question_type === 'table') {
            console.log(
                `Sincronizando tabela para a pergunta: ${currentQuestion.id}`
            )
            const isBooleanTable =
                (currentQuestion as any).type.table.type ===
                'boolean_description'
            const tableItems = (currentQuestion as any).type.table.items
            let initialTableData

            if (
                currentQuestion.answer_content?.table &&
                currentQuestion.answer_content.table.length > 0
            ) {
                initialTableData = currentQuestion.answer_content.table
            } else if (tableItems && tableItems.length > 0) {
                initialTableData = [
                    tableItems.map((item: {label: string}, index: number) =>
                        createEmptyRow(index + 1, isBooleanTable, item.label)
                    ),
                ]
            } else {
                initialTableData = [[createEmptyRow(1, isBooleanTable)]]
            }

            setNewTables(initialTableData)
        }
    }, [activeStep, questionArray])

    useEffect(() => {
        console.log('Verificando perguntas', questionArray)

        if (
            !questionArray ||
            questionArray.length === 0 ||
            (questionArray.length === 1 && (questionArray[0] as any) === '')
        ) {
            setIsLoading(false)
            setFinished(true)
            ResumeDoc()
        }
    }, [questionArray])

    const [resume, setResume] = useState<ResumeItem[]>([])
    const [documents, setDocuments] = useState<Project | undefined>()

    const calculateRowTotal = (
        quantidade: string,
        precounitario: string
    ): string => {
        const qtd = parseFloat(quantidade) || 0
        const price = parseCurrencyInput(precounitario)
        return formatPriceInput((qtd * price).toFixed(2))
    }

    const calculateTableTotal = (table: RowData[]): number => {
        return table.reduce((sum, row) => {
            return sum + parseFloat(row.total_price || '0')
        }, 0)
    }

    const calculateGlobalTotal = (): number => {
        return newTables.reduce((sum, table) => {
            return sum + calculateTableTotal(table)
        }, 0)
    }

    const getCurrentAnswer = (question: QuestionType) => {
        const storedAnswer = formData[question.id]

        if (storedAnswer !== undefined) {
            return storedAnswer
        }

        if (
            question.question_type === 'text' ||
            question.question_type === 'radio' ||
            question.question_type === 'select'
        ) {
            return question.answer_content?.text ?? ''
        }

        if (question.question_type === 'multiselect') {
            return question.answer_content?.options ?? []
        }

        if (question.question_type === 'document_upload') {
            return question.answer_content?.documents ?? []
        }

        return storedAnswer
    }

    const buildAnswerContent = (question: QuestionType) => {
        const questionType = question.question_type
        const selectedValue = getCurrentAnswer(question)
        const content: any = {}

        if (questionType === 'select') {
            const options = question.type.select?.options || []
            if (question.type.select?.multiple) {
                const selectedValues = (selectedValue as string[]) || []
                content.text = selectedValues
                content.indices = selectedValues
                    .map((value) => {
                        const option = options.find(
                            (opt) => opt.content === value
                        )
                        return option?.index
                    })
                    .filter(Boolean)
            } else {
                const selectedOption = options.find(
                    (option) => option.content === selectedValue
                )
                if (selectedOption) {
                    content.text = selectedOption.content
                    content.index = selectedOption.index
                }
            }
        } else if (questionType === 'table') {
            content.table = newTables.map((table) =>
                table.map(({id, ...rest}) => rest)
            )
        } else if (questionType === 'multiselect') {
            content.options = selectedValue
        } else if (questionType === 'document_upload') {
            content.documents = Array.isArray(selectedValue)
                ? selectedValue
                : []
        } else {
            content[questionType] = selectedValue
        }

        return content
    }

    const isCurrentQuestionAnswered = (): boolean => {
        if (!questionArray[activeStep]) return false

        const currentQuestion = questionArray[activeStep] as QuestionType

        if (currentQuestion.optional) {
            return true
        }

        const questionId = currentQuestion.id
        const questionType = currentQuestion.question_type
        const currentAnswer = getCurrentAnswer(currentQuestion)

        switch (questionType) {
            case 'text':
                return Boolean(
                    currentAnswer && (currentAnswer as string).trim()
                )

            case 'radio':
                return Boolean(currentAnswer)

            case 'select':
                if (currentQuestion.type.select?.multiple) {
                    return (
                        Array.isArray(currentAnswer) && currentAnswer.length > 0
                    )
                }
                return Boolean(currentAnswer && currentAnswer !== '')

            case 'multiselect':
                return Array.isArray(currentAnswer) && currentAnswer.length > 0

            case 'document_upload':
                return (
                    Array.isArray(currentAnswer) &&
                    !documentUploadPendingByQuestionId[questionId] &&
                    canSubmitDocumentUploadAnswer(
                        currentAnswer as UploadedDocumentContent[]
                    )
                )

            case 'table':
                if (
                    (currentQuestion as any).type.table.type ===
                    'boolean_description'
                ) {
                    return newTables.every(
                        (table) =>
                            table.length > 0 && table.every((row) => row.bool)
                    )
                }
                return newTables.every(
                    (table) =>
                        table.length > 0 &&
                        table.every(
                            (row) =>
                                row.name &&
                                row.quantity &&
                                row.price &&
                                parseFloat(row.quantity) > 0 &&
                                parseCurrencyInput(row.price) > 0
                        )
                )

            default:
                return false
        }
    }

    const isCurrentDocumentUploadPending = (): boolean => {
        const currentQuestion = questionArray[activeStep] as QuestionType | undefined

        return Boolean(
            currentQuestion?.question_type === 'document_upload' &&
                documentUploadPendingByQuestionId[currentQuestion.id]
        )
    }

    const syncActiveQuestion = (question: QuestionType) => {
        setQuestionArray((currentQuestions) => {
            const nextQuestions = [...currentQuestions]
            const questionIndex = nextQuestions.findIndex(
                (item) => (item as QuestionType)?.id === question.id
            )

            if (questionIndex >= 0) {
                nextQuestions[questionIndex] = question as any
                return nextQuestions
            }

            nextQuestions[activeStep] = question as any
            return nextQuestions
        })

        if (question.question_type === 'document_upload') {
            const documents = question.answer_content?.documents || []

            setFormData((currentFormData) => ({
                ...currentFormData,
                [question.id]: documents,
            }))
            setDocumentUploadPendingByQuestionId((current) => ({
                ...current,
                [question.id]: hasProcessingDocumentUpload(question),
            }))
        }
    }

    useEffect(() => {
        const currentQuestion = questionArray[activeStep] as
            | QuestionType
            | undefined

        const effectiveProjectId = projectId || currentQuestion?.project_id

        if (
            !effectiveProjectId ||
            currentQuestion?.question_type !== 'document_upload'
        ) {
            return
        }

        const docs = formData[currentQuestion.id]
        const isProcessing =
            Array.isArray(docs) &&
            docs.some(
                (document) =>
                    getDocumentUploadStatus(
                        document as UploadedDocumentContent
                    ) === 'processing'
            )

        if (!isProcessing) {
            return
        }

        let cancelled = false
        const intervalId = window.setInterval(async () => {
            try {
                // Atualiza o status/conteudo da PERGUNTA ATUAL (sem avancar o fluxo):
                // assim o "Proximo" habilita quando processar, mas quem avanca e o usuario.
                const refreshed = await api.getQuestionById(
                    effectiveProjectId,
                    currentQuestion.id
                )

                if (cancelled) {
                    return
                }

                const serverDocuments = (refreshed as any)?.answer_content
                    ?.documents

                if (Array.isArray(serverDocuments)) {
                    setFormData((prev) => ({
                        ...prev,
                        [currentQuestion.id]: serverDocuments,
                    }))
                    const stillProcessing = serverDocuments.some(
                        (document: UploadedDocumentContent) =>
                            getDocumentUploadStatus(document) === 'processing'
                    )
                    setDocumentUploadPendingByQuestionId((current) => ({
                        ...current,
                        [currentQuestion.id]: stillProcessing,
                    }))
                }
            } catch (error) {
                console.error('Erro ao consultar processamento do documento:', error)
            }
        }, 3000)

        return () => {
            cancelled = true
            window.clearInterval(intervalId)
        }
    }, [activeStep, api, projectId, questionArray, formData])

    useEffect(() => {
        console.log('useEffect')
        if (finish) {
            const timeout = setTimeout(() => {
                handleNext()
            }, 1000)

            return () => clearTimeout(timeout)
        }
    }, [finish])

    useEffect(() => {
        const storedProjectId = localStorage.getItem('projectId')
        setProjectId(storedProjectId?.replace(/"/g, '') || '')
        const storedQuestionArray = localStorage.getItem('questionArray')
        if (questionArray.length > 0) {
            setIsLoading(false)
        } else {
            if (storedQuestionArray) {
                setQuestionArray(JSON.parse(storedQuestionArray))

                setProjectName(
                    localStorage.getItem('projectName')?.replace(/"/g, '') || ''
                )
            }
            if (storedQuestionArray?.length === 4) {
                setFinished(true)
                ResumeDoc()
            }

            setIsLoading(false)
        }
        if (questionArray[activeStep]?.answer_content?.table) {
            setNewTables(questionArray[activeStep]?.answer_content?.table)
        }
    }, [questionArray, setQuestionArray])

    const ResumeDoc = async () => {
        const projectId =
            localStorage.getItem('projectId')?.replace(/"/g, '') || ''
        const res = await api.getResume(projectId)
        const resDocuments = await api.getDocuments(projectId)
        setResume(res)
        setDocuments(resDocuments)
        console.log('documentos', resDocuments)
        console.log(resume)
    }

    const handleBack = async () => {
        if (questionArray[activeStep].progress.answered === 0) {
            navigate('/projetos')
            return
        }
        try {
            setIsLoading(true)
            const currentQuestion = questionArray[activeStep] as QuestionType
            const questionId = currentQuestion.id
            const projectGuid = currentQuestion.project_id
            const content = buildAnswerContent(currentQuestion)

            const res = await api.getPreviousQuestion(
                projectGuid,
                questionId,
                {
                    content: content,
                    question_id: questionId,
                    project_id: projectGuid,
                    answered: isAnswerContentEmpty(content) ? "false" : "true",
                }
            )
            setQuestionArray([...questionArray, res])
            handleChange(res.id, res.answer_content.text)
        } catch (error) {
            console.error(error)
            navigate('/projetos')
        }
        setIsLoading(false)
        setActiveStep((prevStep) => prevStep + 1)
    }

    const handleNext = async () => {
        if (isCurrentDocumentUploadPending()) {
            return
        }

        if (
            limits.usages.projects.max - limits.usages.projects.current <=
            projectCost
        ) {
            setShowAlert(true)
        } else {
            setShowAlert(false)
        }
        console.log(finish)
        if (activeStep === questionArray.length - 1) {
            const currentQuestion = questionArray[activeStep] as QuestionType
            const questionId = currentQuestion.id
            const projectGuid = currentQuestion.project_id
            const content = buildAnswerContent(currentQuestion)

            try {
                setIsLoading(true)
                const res = await api.postAnswer({
                    content: content,
                    question_id: questionId,
                    project_id: projectGuid,
                })

                if (res && hasProcessingDocumentUpload(res as QuestionType)) {
                    syncActiveQuestion(res as QuestionType)
                    setIsLoading(false)
                    return
                }

                if (!res) {
                    setFinished(true)
                    setEdit(false)
                    setFinish(false)
                    clearEditingContext()
                    ResumeDoc()
                }
                if (
                    res &&
                    res.answer_content?.text == null &&
                    res.answer_content?.table == null &&
                    edit
                ) {
                    console.log('resposta vazia')
                    setTimeout(() => {
                        setFinish(false)
                        setEdit(false)
                    }, 1000)
                    console.log('valor de finish:', finish)
                } else if (finish) {
                    console.log('finalizando')
                    setFinished(true)
                    setFinish(false)
                    setEdit(false)
                    clearEditingContext()
                    ResumeDoc()
                }
                setQuestionArray([...questionArray, res])
                setActiveStep((prevStep) => prevStep + 1)
                setIsLoading(false)
            } catch (error) {
                console.log('deu erro')
                setIsLoading(false)
                if (error) {
                    navigate('/projetos')
                } else {
                    setViewResume(false)
                    setFinished(true)
                    ResumeDoc()
                }
            }
            return
        }

        setActiveStep((prevStep) => prevStep + 1)
    }

    const handleChange = (
        id: string,
        value: FormDataValue
    ) => {
        const currentQuestion = questionArray[activeStep] as QuestionType

        if (
            currentQuestion.question_type === 'multiselect' ||
            currentQuestion.question_type === 'checkbox'
        ) {
            const currentValues = (formData[id] as string[]) || []
            const valueStr = value as string

            let newValues: string[]

            if (currentValues.includes(valueStr)) {
                newValues = currentValues.filter((v) => v !== valueStr)
            } else {
                newValues = [...currentValues, valueStr]
            }

            setFormData((prevData) => ({
                ...prevData,
                [id]: newValues,
            }))
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [id]: value,
            }))
        }
    }

    const handleDocumentUploadPendingChange = (
        id: string,
        pending: boolean
    ) => {
        setDocumentUploadPendingByQuestionId((current) => ({
            ...current,
            [id]: pending,
        }))
    }

    const addNewTable = () => {
        const currentQuestion = questionArray[activeStep] as QuestionType
        const isBooleanTable =
            (currentQuestion as any).type.table.type === 'boolean_description'
        const newRowOrRows = createEmptyRow(1, isBooleanTable)
        const newTable = Array.isArray(newRowOrRows)
            ? newRowOrRows
            : [newRowOrRows]
        setNewTables([...newTables, newTable])
    }

    const deleteTable = (tableIndex: number) => {
        if (tableIndex === 0) {
            toast.error('Não é possível excluir o primeiro lote')
            return
        }
        setNewTables((prevTables) =>
            prevTables.filter((_, index) => index !== tableIndex)
        )
    }

    const addRowToTable = (tableIndex: number) => {
        setNewTables((prevTables) => {
            const updatedTables = [...prevTables]
            const newRow = createEmptyRow(updatedTables[tableIndex].length + 1)
            if (Array.isArray(newRow)) {
                updatedTables[tableIndex] = [
                    ...updatedTables[tableIndex],
                    ...newRow,
                ]
            } else {
                updatedTables[tableIndex] = [
                    ...updatedTables[tableIndex],
                    newRow,
                ]
            }
            return updatedTables
        })
    }
    const finishProject = () => {
        setGeneratingProject(true)
        clearEditingContext()
        const projectId =
            localStorage.getItem('projectId')?.replace(/"/g, '') || ''
        api.finishProject(projectId)
            .then(() => {
                setFinished(true)
                navigate('/projetos')
            })
            .catch((error) => {
                console.error(error)
                setFinished(true)
            })
    }

    const deleteRowFromTable = (tableIndex: number, rowId: number) => {
        setNewTables((prevTables) => {
            const updatedTables = prevTables.map((table, index) =>
                index === tableIndex
                    ? table.filter((row) => row.id !== rowId)
                    : table
            )
            return updatedTables
        })
    }

    const editQuestion = async (questionId: string) => {
        const res = await api.getQuestionById(projectId, questionId)
        localStorage.setItem(EDITING_MODE_KEY, 'true')
        localStorage.setItem(EDITING_PROJECT_ID_KEY, JSON.stringify(projectId))
        localStorage.setItem(EDITING_QUESTION_ID_KEY, JSON.stringify(questionId))
        setActiveStep(0)
        setQuestionArray([res])
        setFinished(false)
        setEdit(true)
    }

    const formatPriceInput = (value: string): string => {
        let numbers = value.replace(/[^\d]/g, '')

        if (!numbers) return ''

        const numberInCents = parseInt(numbers)
        const formattedValue = (numberInCents / 100)
            .toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
            .replace(/\./g, '')

        return formattedValue
    }

    const handleInputChangeTable = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        tableIndex: number,
        rowIndex: number,
        field: keyof RowData
    ) => {
        const {value} = event.target

        setNewTables((prevTables) => {
            const updatedTables = [...prevTables]
            const currentRow = {...updatedTables[tableIndex][rowIndex]}

            const currentQuestion = questionArray[activeStep] as QuestionType
            const isBooleanTable =
                (currentQuestion as any).type.table.type ===
                'boolean_description'

            if (isBooleanTable) {
                if (field === 'bool') {
                    currentRow.bool = value
                } else {
                    ;(currentRow[field] as string) = value
                }
            } else {
                if (field === 'price') {
                    currentRow[field] = formatPriceInput(value)
                    currentRow.total_price = calculateRowTotal(
                        currentRow.quantity || '0',
                        currentRow.price || '0'
                    )
                } else {
                    ;(currentRow[field] as string) = value
                    if (field === 'quantity') {
                        currentRow.total_price = calculateRowTotal(
                            currentRow.quantity || '0',
                            currentRow.price || '0'
                        )
                    }
                }
            }

            updatedTables[tableIndex][rowIndex] = currentRow
            return updatedTables
        })
    }

    if (isLoading) {
        return (
            <Page>
                <Header>
                    <Typography variant="h5" sx={{fontWeight: 700, color: '#1f2b3a'}}>{projectName}</Typography>
                    <Typography variant="body2" sx={{color: '#5b6b7b', mb: 3}}>
                        Para seguir com novo documento precisamos que você
                        responda um breve formulário
                    </Typography>
                </Header>
                <ContentContainer>
                    <Typography variant="h6">Carregando...</Typography>
                </ContentContainer>
            </Page>
        )
    }

    if (finished) {
        return (
            <DocumentResume
                projectName={projectName}
                resume={resume}
                showAlert={showAlert}
                generatingProject={generatingProject}
                onEditQuestion={editQuestion}
                onFinishProject={finishProject}
                documents={documents}
                onlyView={viewResume}
            />
        )
    }

    return (
        <Page>
            <Header>
                <Typography variant="h5" sx={{fontWeight: 700, color: '#1f2b3a'}}>{projectName}</Typography>
                <Typography variant="body2" sx={{color: '#5b6b7b', mb: 3}}>
                    Para seguir com novo documento precisamos que você responda
                    um breve formulário
                </Typography>
                {showAlert && (
                    <Alert severity="error">
                        Os seus créditos de Documentos acabaram.
                    </Alert>
                )}
            </Header>
            <ContentContainer key={`question-${questionArray[activeStep]?.id}`}>
                <IconButton
                    onClick={() => navigate('/projetos')}
                    sx={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        zIndex: 10,
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Typography
                    variant="subtitle2"
                    sx={{color: 'gray', fontSize: '0.875rem', marginTop: '4px'}}
                >
                    {`Pergunta ${questionArray[activeStep]?.progress.answered} de ${questionArray[activeStep]?.progress.total}`}
                </Typography>
                <Typography
                    variant="h6"
                    sx={{
                        marginBottom: '24px',
                    }}
                >
                    <span
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            alignContent: 'center',
                        }}
                    >
                        {questionArray[activeStep]?.content}{' '}
                        {(questionArray[activeStep] as QuestionType | undefined)
                            ?.optional && (
                            <span
                                style={{
                                    marginLeft: '5px',
                                    fontSize: '0.8em',
                                    color: 'gray',
                                }}
                            >
                                (opcional)
                            </span>
                        )}
                        {questionArray[activeStep]?.tooltip && (
                            <Tooltip
                                style={{
                                    marginLeft: '5px',
                                    marginTop: '2px',
                                }}
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            maxWidth: '400px',
                                            fontSize: '16px',
                                            padding: '12px 16px',
                                            backgroundColor: '#333',
                                            color: '#fff',
                                        },
                                    },
                                }}
                                title={questionArray[activeStep]?.tooltip || ''}
                                arrow
                                placement="top"
                            >
                                <InfoIcon fontSize="small" color="info" />
                            </Tooltip>
                        )}
                    </span>
                </Typography>
                {questionArray[activeStep] && (
                    <QuestionRenderer
                        question={questionArray[activeStep] as QuestionType}
                        projectId={projectId}
                        formData={formData}
                        setFormData={setFormData}
                        onChange={handleChange}
                        onDocumentUploadPendingChange={
                            handleDocumentUploadPendingChange
                        }
                        onNext={handleNext}
                        isCurrentQuestionAnswered={isCurrentQuestionAnswered}
                        api={api}
                        newTables={newTables}
                        onAddTable={addNewTable}
                        onAddRow={addRowToTable}
                        onDeleteTable={deleteTable}
                        onDeleteRow={deleteRowFromTable}
                        onInputChangeTable={handleInputChangeTable}
                        calculateTableTotal={calculateTableTotal}
                        calculateGlobalTotal={calculateGlobalTotal}
                        formatCurrency={formatCurrency}
                        colors={COLORS}
                        user={user}
                    />
                )}
            </ContentContainer>
            <NavigationButtons>
                <div>
                    {questionArray[activeStep]?.progress.answered !== 1 &&
                        !edit && (
                            <Button
                                style={{
                                    color: COLORS.botaoFormulario,
                                }}
                                onClick={handleBack}
                                disabled={isLoading}
                                sx={{mr: 1}}
                            >
                                Voltar
                            </Button>
                        )}
                    {edit && (
                        <Button
                            style={{
                                backgroundColor: COLORS.botaoFormulario,
                                color: 'white',
                            }}
                            onClick={() => setFinished(true)}
                        >
                            {' '}
                            Cancelar{' '}
                        </Button>
                    )}
                </div>
                <div
                    style={{
                        display: 'flex',
                        gap: '8px',
                        flexDirection: 'row',
                    }}
                >
                    <Button
                        style={{
                            backgroundColor: COLORS.botaoFormulario,
                            color: 'white',
                        }}
                        variant="contained"
                        onClick={() => {
                            setFinish(false)
                            setTimeout(() => {
                                handleNext()
                            }, 100)
                        }}
                        disabled={!isCurrentQuestionAnswered()}
                    >
                        Próximo
                    </Button>

                    {edit && (
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setFinish(true)
                            }}
                            disabled={!isCurrentQuestionAnswered()}
                        >
                            Finalizar
                        </Button>
                    )}
                </div>
            </NavigationButtons>
        </Page>
    )
}
